<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useCurrency } from 'dashboard/composables/useCurrency';

import AddSalesGoal from './AddSalesGoal.vue';
import EditSalesGoal from './EditSalesGoal.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const getters = useStoreGetters();
const store = useStore();
const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedGoal = ref({});

const records = computed(() => getters['salesGoals/getSalesGoals'].value);
const uiFlags = computed(() => getters['salesGoals/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedGoal.value.name}?`);

const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(browserLocale.value);
};

const getStatusClass = status => {
  const classes = {
    achieved:
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    on_track:
      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    at_risk:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    behind: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  return classes[status] || classes.on_track;
};

const getStatusLabel = status => {
  const labels = {
    achieved: 'SALES_GOALS_MGMT.STATUS.ACHIEVED',
    on_track: 'SALES_GOALS_MGMT.STATUS.ON_TRACK',
    at_risk: 'SALES_GOALS_MGMT.STATUS.AT_RISK',
    behind: 'SALES_GOALS_MGMT.STATUS.BEHIND',
  };
  return t(labels[status] || labels.on_track);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = goal => {
  showEditPopup.value = true;
  selectedGoal.value = goal;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = goal => {
  showDeleteConfirmationPopup.value = true;
  selectedGoal.value = goal;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteSalesGoal = async id => {
  try {
    await store.dispatch('salesGoals/delete', id);
    useAlert(t('SALES_GOALS_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('SALES_GOALS_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedGoal.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedGoal.value.id] = true;
  closeDeletePopup();
  deleteSalesGoal(selectedGoal.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('SALES_GOALS_MGMT.LIST.TABLE_HEADER.NAME'),
    t('SALES_GOALS_MGMT.LIST.TABLE_HEADER.PERIOD'),
    t('SALES_GOALS_MGMT.LIST.TABLE_HEADER.TARGET'),
    t('SALES_GOALS_MGMT.LIST.TABLE_HEADER.PROGRESS'),
    t('SALES_GOALS_MGMT.LIST.TABLE_HEADER.OWNER'),
    t('SALES_GOALS_MGMT.LIST.TABLE_HEADER.STATUS'),
  ];
});

onBeforeMount(() => {
  store.dispatch('salesGoals/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('SALES_GOALS_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('SALES_GOALS_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SALES_GOALS_MGMT.HEADER')"
        :description="$t('SALES_GOALS_MGMT.DESCRIPTION')"
        feature-name="sales_goals"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('SALES_GOALS_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="goal in records" :key="goal.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col">
                <span class="font-medium text-n-slate-12">
                  {{ goal.name }}
                </span>
                <span v-if="!goal.is_active" class="text-xs text-n-slate-10">
                  ({{ $t('SALES_GOALS_MGMT.STATUS.INACTIVE') }})
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col text-sm">
                <span class="text-n-slate-12 capitalize">
                  {{ goal.period_type }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ formatDate(goal.period_start) }} -
                  {{ formatDate(goal.period_end) }}
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col">
                <span class="font-medium text-n-slate-12">
                  {{ formatCurrency(goal.target_value) }}
                </span>
                <span v-if="goal.target_deals" class="text-xs text-n-slate-10">
                  {{ goal.target_deals }} deals
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <div
                    class="w-24 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden"
                  >
                    <div
                      class="h-full rounded-full transition-all"
                      :class="
                        goal.progress?.value >= 100
                          ? 'bg-green-500'
                          : 'bg-blue-500'
                      "
                      :style="{
                        width: `${Math.min(goal.progress?.value || 0, 100)}%`,
                      }"
                    />
                  </div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ goal.progress?.value || 0 }}%
                  </span>
                </div>
                <span class="text-xs text-n-slate-10">
                  {{ formatCurrency(goal.current_value) }} /
                  {{ formatCurrency(goal.target_value) }}
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div v-if="goal.user" class="flex items-center gap-2">
                <Avatar
                  :src="goal.user.avatar_url"
                  :name="goal.user.name"
                  :size="24"
                  rounded-full
                />
                <span class="text-sm text-n-slate-12">
                  {{ goal.user.name }}
                </span>
              </div>
              <span v-else class="text-sm text-n-slate-10 italic">
                {{ $t('SALES_GOALS_MGMT.OWNER.TEAM') }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="px-2 py-1 text-xs font-medium rounded-full"
                :class="getStatusClass(goal.status)"
              >
                {{ getStatusLabel(goal.status) }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('SALES_GOALS_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[goal.id]"
                  @click="openEditPopup(goal)"
                />
                <Button
                  v-tooltip.top="$t('SALES_GOALS_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[goal.id]"
                  @click="openDeletePopup(goal)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddSalesGoal @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditSalesGoal :selected-goal="selectedGoal" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('SALES_GOALS_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('SALES_GOALS_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('SALES_GOALS_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('SALES_GOALS_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
